import React from 'react';
import { API_BASE } from '../api/config';

const API_BASE_URL = `${API_BASE}/api`;

interface ContractorProfileHeaderEditableProps {
  profilePictureUrl: string;
  onProfilePictureUrlChange: (url: string) => void;
  companyName: string;
  onCompanyNameChange: (name: string) => void;
  isVerified: boolean;
}

const ContractorProfileHeaderEditable: React.FC<ContractorProfileHeaderEditableProps> = ({
  profilePictureUrl,
  onProfilePictureUrlChange,
  companyName,
  onCompanyNameChange,
  isVerified,
}) => {
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/Upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      onProfilePictureUrlChange(data.url);
    } catch {
      setUploadError('Photo upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row items-center md:items-start mb-8 border-b pb-6 gap-6">
      <div className="relative w-32 h-32 flex-shrink-0">
        <img
          className="w-32 h-32 rounded-full object-cover border border-gray-200"
          src={profilePictureUrl || `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='128' height='128' viewBox='0 0 128 128'%3E%3Crect width='128' height='128' fill='%23e5e7eb'/%3E%3Ccircle cx='64' cy='50' r='24' fill='%239ca3af'/%3E%3Cellipse cx='64' cy='110' rx='40' ry='28' fill='%239ca3af'/%3E%3C/svg%3E`}
          alt="Profile"
        />
        <input type="file" id="profile-picture-upload" accept="image/*" className="hidden" onChange={handleFileChange} />
        <label
          htmlFor="profile-picture-upload"
          className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-2 hover:bg-blue-700 cursor-pointer"
        >
          {uploading ? (
            <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
        </label>
      </div>
      <div className="flex-grow text-center md:text-left">
        <input
          type="text"
          placeholder="Company name"
          className="text-3xl font-bold text-gray-800 mb-2 w-full border-b border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent"
          value={companyName}
          onChange={(e) => onCompanyNameChange(e.target.value)}
        />
        {isVerified && (
          <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded-full mt-2">
            Verified
          </span>
        )}
        {uploadError && <p className="text-red-500 text-xs mt-1">{uploadError}</p>}
      </div>
    </div>
  );
};

export default ContractorProfileHeaderEditable;
